<template>
  <Modal :open="open" @closed="closeModal" dialog-size="modal-lg">
    <div class="modal-header border-0">
      <h5 class="modal-title m-0">{{ isEditing ? 'Edit Review Criteria' : 'Add Review Criteria' }}</h5>
      <button @click="closeModal" type="button" class="btn-close outline-none"></button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="save">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Section <span class="text-danger">*</span></label>
            <select
              v-model="localForm.section"
              class="form-select"
              required
              @change="onSectionChange"
              :disabled="saving"
            >
              <option value="" disabled>-- Select Section --</option>
              <option
                v-for="section in sections"
                :key="section.title"
                :value="section.title"
              >
                {{ section.title }}
              </option>
              <option v-if="sections.length === 0" value="" disabled>No sections available</option>
            </select>
            <small v-if="sections.length === 0" class="text-muted">No sections defined in this template</small>
          </div>

          <div class="col-12">
            <label class="form-label">Question <span class="text-danger">*</span></label>
            <input
              v-model="localForm.question"
              type="text"
              class="form-control"
              required
              placeholder="Enter review question"
            />
          </div>

          <div class="col-12">
            <label class="form-label">Description</label>
            <textarea
              v-model="localForm.description"
              class="form-control"
              rows="2"
              placeholder="Short description (optional)"
            ></textarea>
          </div>

          <div class="col-md-4">
            <label class="form-label">Answer Type <span class="text-danger">*</span></label>
            <select
              v-model="localForm.answer_type"
              class="form-select"
              required
            >
              <option value="rating">Rating</option>
              <option value="text">Text</option>
              <option value="boolean">Boolean</option>
              <option value="multiple_choice">Multiple Choice</option>
            </select>
          </div>

<!--          <div class="col-md-4">-->
<!--            <label class="form-label">Max Score</label>-->
<!--            <input-->
<!--              v-model.number="localForm.max_score"-->
<!--              type="number"-->
<!--              min="1"-->
<!--              class="form-control"-->
<!--              placeholder="e.g. 5"-->
<!--            />-->
<!--          </div>-->

          <div class="col-md-4">
            <label class="form-label">Weight</label>
            <input
              v-model.number="localForm.weight"
              type="number"
              min="0"
              class="form-control"
              placeholder="e.g. 10"
            />
          </div>

          <div class="col-md-4">
            <label class="form-label">Order</label>
            <input
              v-model.number="localForm.order"
              type="number"
              min="0"
              class="form-control"
              placeholder="Display order"
            />
          </div>

          <!-- Options for multiple_choice -->
          <div class="col-12" v-if="localForm.answer_type === 'multiple_choice'">
            <label class="form-label">Options</label>
            <div
              v-for="(opt, idx) in localForm.options"
              :key="idx"
              class="input-group mb-2"
            >
              <input
                v-model="localForm.options[idx]"
                type="text"
                class="form-control"
                :placeholder="'Option ' + (idx + 1)"
              />
              <button
                type="button"
                class="btn btn-outline-danger"
                @click="removeOption(idx)"
                :disabled="localForm.options.length <= 1"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="addOption"
            >
              <i class="bi bi-plus me-1"></i> Add Option
            </button>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-outline-secondary me-2"
            @click="closeModal"
            :disabled="saving"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="saving || !isFormValid"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/modal/Modal.vue'

export default {
  name: 'AddEditCriteriaModal',
  components: {
    Modal
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    sections: {
      type: Array,
      default: () => []
    },
    templates: {
      type: Array,
      default: () => []
    },
    selectedTemplateId: {
      type: [String, Number],
      default: ''
    },
    selectedTemplateName: {
      type: String,
      default: ''
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:selectedTemplateId'],

  data() {
    return {
      localForm: {
        review_template_id: '',
        section: '',
        category: '',
        question: '',
        description: '',
        answer_type: 'rating',
        options: [],
        max_score: 5, // Default max score
        weight: 1,    // Default weight
        order: 0      // Default order
      },
    }
  },
  computed: {
    isFormValid() {
      const hasSection = !!this.localForm.section?.trim()
      const hasQuestion = !!this.localForm.question?.trim()
      const hasAnswerType = !!this.localForm.answer_type

      // For multiple choice, we need at least one non-empty option
      const validOptions = this.localForm.answer_type !== 'multiple_choice' ||
                         (Array.isArray(this.localForm.options) &&
                          this.localForm.options.some(opt => opt?.trim() !== ''))

      // For rating, ensure max_score is a positive number
      const validScore = this.localForm.answer_type !== 'rating' ||
                        (this.localForm.max_score !== null &&
                         this.localForm.max_score > 0)

      return hasSection && hasQuestion && hasAnswerType && validOptions && validScore
    }
  },
  watch: {
    selectedTemplateId: {
      handler(newVal) {
        this.$emit('update:selectedTemplateId', newVal)
      }
    },
    formData: {
      handler(newVal) {
        if (newVal) {
          this.localForm = {
            review_template_id: newVal.review_template_id || '',
            section: newVal.section || '',
            question: newVal.question || '',
            description: newVal.description || '',
            answer_type: newVal.answer_type || 'rating',
            options: Array.isArray(newVal.options) ? [...newVal.options] : [],
            max_score: newVal.max_score ?? 5,
            weight: newVal.weight ?? 1,
            order: newVal.order ?? 0
          }
        }
      },
      deep: true,
      immediate: true
    },
    open(newVal) {
      if (!newVal) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.localForm = {
        review_template_id: this.formData.review_template_id || '',
        section: this.formData.section || '',
        question: this.formData.question || '',
        description: this.formData.description || '',
        answer_type: this.formData.answer_type || 'rating',
        options: Array.isArray(this.formData.options) ? [...this.formData.options] : [],
        max_score: this.formData.max_score ?? 5,
        weight: this.formData.weight ?? 1,
        order: this.formData.order ?? 0
      };
    },
    addOption() {
      this.localForm.options.push('');
    },
    removeOption(index) {
      if (this.localForm.options.length > 1) {
        this.localForm.options.splice(index, 1);
      }
    },
    onSectionChange() {
      // Reset any section-specific state if needed
    },
    closeModal() {
      if (!this.saving) {
        this.$emit('close');
      }
    },
    save() {
      if (!this.isFormValid || this.saving) return;

      const payload = {
        ...this.localForm,
        // Clean up the options array for non-multiple choice questions
        ...(this.localForm.answer_type !== 'multiple_choice' && { options: [] })
      };

      this.$emit('save', payload);
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 500;
}

.text-danger {
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
